import { useState, useEffect, useCallback, useRef } from 'react'
import { useUser } from '@clerk/clerk-react'

// useUserPref(key, defaultValue)
//
// Reads/writes a key in public.user_prefs via /api/user-prefs when the user
// is signed in. Falls back to localStorage (with the same key namespace)
// when SignedOut so the UI still remembers the selection within the device.
//
// Optimistic on write: setValue updates local state immediately, then
// fire-and-forgets the network call. Most callers (sort buttons, period
// picker) want zero-perceived-latency UX.
export function useUserPref(key, defaultValue) {
  const { user, isLoaded } = useUser()
  const userId = user?.id || null
  const [value, setValue] = useState(defaultValue)
  const [hydrated, setHydrated] = useState(false)
  const keyRef = useRef(key)
  keyRef.current = key

  // Hydrate from server (signed in) or localStorage (signed out).
  useEffect(() => {
    if (!isLoaded) return
    let cancelled = false
    if (userId) {
      fetch(`/api/user-prefs?user_id=${encodeURIComponent(userId)}&key=${encodeURIComponent(key)}`)
        .then((r) => r.ok ? r.json() : null)
        .then((j) => {
          if (cancelled) return
          if (j && j.value != null) {
            setValue(j.value)
            // Device-local mirror of the last-known server value, so
            // launch-time readers (the default_tab boot redirect in App.jsx)
            // can read prefs synchronously before Clerk/network hydrate.
            try { localStorage.setItem(`atlas.pref.${key}`, JSON.stringify(j.value)) } catch { /* private mode */ }
          }
          setHydrated(true)
        })
        .catch(() => { if (!cancelled) setHydrated(true) })
    } else {
      try {
        const stored = localStorage.getItem(`atlas.pref.${key}`)
        if (stored != null) setValue(JSON.parse(stored))
      } catch {}
      setHydrated(true)
    }
    return () => { cancelled = true }
  }, [isLoaded, userId, key])

  const write = useCallback((next) => {
    setValue(next)
    // Always mirror locally (signed-in included) — see hydrate note above.
    try { localStorage.setItem(`atlas.pref.${keyRef.current}`, JSON.stringify(next)) } catch { /* private mode */ }
    if (userId) {
      fetch('/api/user-prefs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, key: keyRef.current, value: next }),
      }).catch((e) => console.error('[useUserPref] write failed', e))
    }
  }, [userId])

  return [value, write, hydrated]
}
